import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { WorkflowStep } from "@/types/workflow";
import { WorkflowStepStatus } from "./WorkflowStepStatus";
import { WorkflowStepActions } from "./WorkflowStepActions";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface WorkflowStepItemProps {
  step: WorkflowStep;
  caseId: string;
  updating: boolean;
  onStatusUpdate: (newStatus: WorkflowStep["status"]) => Promise<void>;
  onAIHelp: (step: WorkflowStep) => void;
  onGenerateDocument: (step: WorkflowStep) => void;
  isLoadingAI?: boolean;
  isGeneratingDoc?: boolean;
}

export function WorkflowStepItem({
  step,
  caseId,
  updating,
  onStatusUpdate,
  onAIHelp,
  onGenerateDocument,
  isLoadingAI = false,
  isGeneratingDoc = false
}: WorkflowStepItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden bg-card/50 w-full max-w-full transition-all duration-200",
      "hover:bg-accent/5",
      isMobile && "mx-0"
    )}>
      <div
        className={cn(
          "flex items-start gap-3 p-4 cursor-pointer w-full transition-colors",
          isExpanded && "border-b",
          isMobile && "p-3"
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="mt-1 shrink-0">
          {isExpanded ? (
            <ChevronDown className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isMobile && "h-3.5 w-3.5"
            )} />
          ) : (
            <ChevronRight className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isMobile && "h-3.5 w-3.5"
            )} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h4 className={cn(
              "font-medium leading-tight break-words flex-1",
              isMobile ? "text-sm" : "text-base"
            )}>
              {step.title}
            </h4>
            <div className="shrink-0">
              <WorkflowStepStatus
                status={step.status}
                updating={updating}
                onStatusUpdate={onStatusUpdate}
              />
            </div>
          </div>

          {!isExpanded && (
            <p className={cn(
              "text-muted-foreground mt-1.5 break-words line-clamp-2",
              isMobile ? "text-xs leading-normal" : "text-sm leading-relaxed"
            )}>
              {step.description}
            </p>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className={cn(
          "p-4 space-y-4 animate-accordion-down",
          isMobile && "p-3 space-y-3"
        )}>
          <p className={cn(
            "text-muted-foreground break-words",
            isMobile ? "text-xs leading-normal" : "text-sm leading-relaxed"
          )}>
            {step.description}
          </p>

          <WorkflowStepActions
            step={step}
            caseId={caseId}
            updating={updating}
            onStatusUpdate={onStatusUpdate}
            onAIHelp={() => onAIHelp(step)}
            onGenerateDocument={() => onGenerateDocument(step)}
            isLoadingAI={isLoadingAI}
            isGeneratingDoc={isGeneratingDoc}
          />
        </div>
      )}
    </div>
  );
}